import React from 'react';

const FilterSidebar = ({
  genres,
  activeFilter,
  selectedGenre,
  sortBy,
  minRating,
  yearRange,
  onFilterChange,
  onSortChange,
  onRatingChange,
  onYearRangeChange,
  isOpen,
  onClose
}) => {
  const filters = [
    { id: 'popular', label: 'Popular', icon: '🔥', description: 'Most popular movies' },
    { id: 'trending', label: 'Trending', icon: '📈', description: 'Trending this week' },
    { id: 'top_rated', label: 'Top Rated', icon: '⭐', description: 'Highest rated films' },
    { id: 'upcoming', label: 'Coming Soon', icon: '🎬', description: 'Upcoming releases' },
    { id: 'now_playing', label: 'Now Playing', icon: '🎭', description: 'Currently in theaters' },
    { id: 'high_rated', label: 'Premium (8+)', icon: '👑', description: 'Premium quality films' },
  ];

  const sortOptions = [
    { value: 'popularity.desc', label: 'Most Popular' },
    { value: 'vote_average.desc', label: 'Highest Rated' },
    { value: 'release_date.desc', label: 'Newest First' },
    { value: 'release_date.asc', label: 'Oldest First' },
    { value: 'title.asc', label: 'A-Z' },
    { value: 'title.desc', label: 'Z-A' },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-16 bottom-0 w-80 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-transform duration-300 z-50 overflow-y-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span>🎯</span>
              <span>Filters</span>
            </h2>
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Categories */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <span>📂</span>
              <span>Categories</span>
            </h3>
            <div className="space-y-2">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => onFilterChange(filter.id)}
                  className={`w-full text-left p-3 rounded-xl transition-all duration-200 group ${
                    activeFilter === filter.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{filter.icon}</span>
                    <div>
                      <div className="font-medium">{filter.label}</div>
                      <div className={`text-xs ${
                        activeFilter === filter.id ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {filter.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Genres */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <span>🎭</span>
              <span>Genres</span>
            </h3>
            <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
              {genres.map((genre) => (
                <button
                  key={genre.id}
                  onClick={() => onFilterChange('genre', genre)}
                  className={`p-3 text-sm rounded-lg text-left transition-all duration-200 ${
                    selectedGenre?.id === genre.id
                      ? 'bg-purple-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {genre.name}
                </button>
              ))}
            </div>
          </div>

          {/* Sort Options */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <span>🔄</span>
              <span>Sort By</span>
            </h3>
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="w-full p-3 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Rating Filter */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <span>⭐</span>
              <span>Minimum Rating</span>
            </h3>
            <div className="space-y-4">
              <input
                type="range"
                min="0"
                max="10"
                step="0.1"
                value={minRating}
                onChange={(e) => onRatingChange(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">0.0</span>
                <div className="flex items-center space-x-1">
                  <span className="text-lg font-bold text-yellow-500">{minRating.toFixed(1)}</span>
                  <span className="text-yellow-500">⭐</span>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">10.0</span>
              </div>
            </div>
          </div>

          {/* Year Range */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <span>📅</span>
              <span>Release Year</span>
            </h3>
            <div className="space-y-4">
              {/* Min Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  From
                </label>
                <input
                  type="range"
                  min="1900"
                  max={currentYear}
                  value={yearRange.min}
                  onChange={(e) => onYearRangeChange({ ...yearRange, min: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
                  <span>1900</span>
                  <span className="font-bold text-gray-900 dark:text-white">{yearRange.min}</span>
                  <span>{currentYear}</span>
                </div>
              </div>

              {/* Max Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  To
                </label>
                <input
                  type="range"
                  min="1900"
                  max={currentYear}
                  value={yearRange.max}
                  onChange={(e) => onYearRangeChange({ ...yearRange, max: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
                  <span>1900</span>
                  <span className="font-bold text-gray-900 dark:text-white">{yearRange.max}</span>
                  <span>{currentYear}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <span>⚡</span>
              <span>Quick Filters</span>
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  onRatingChange(8.0);
                  onFilterChange('high_rated');
                }}
                className="p-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white rounded-lg text-sm font-medium hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200"
              >
                ⭐ 8.0+ Stars
              </button>
              
              <button
                onClick={() => {
                  onYearRangeChange({ min: currentYear - 1, max: currentYear });
                  onFilterChange('upcoming');
                }}
                className="p-3 bg-gradient-to-r from-blue-400 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-blue-500 hover:to-blue-700 transition-all duration-200"
              >
                🎬 Recent
              </button>
              
              <button
                onClick={() => {
                  onYearRangeChange({ min: 1990, max: 2010 });
                  onRatingChange(7.0);
                }}
                className="p-3 bg-gradient-to-r from-purple-400 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-purple-500 hover:to-purple-700 transition-all duration-200"
              >
                📼 Classics
              </button>
              
              <button
                onClick={() => {
                  onRatingChange(0);
                  onYearRangeChange({ min: 1900, max: currentYear });
                  onFilterChange('popular');
                }}
                className="p-3 bg-gradient-to-r from-gray-400 to-gray-600 text-white rounded-lg text-sm font-medium hover:from-gray-500 hover:to-gray-700 transition-all duration-200"
              >
                🔄 Reset All
              </button>
            </div>
          </div>

          {/* Filter Summary */}
          <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Current Filters</h4>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <div>Category: <span className="font-medium">{filters.find(f => f.id === activeFilter)?.label || 'None'}</span></div>
              {selectedGenre && <div>Genre: <span className="font-medium">{selectedGenre.name}</span></div>}
              {minRating > 0 && <div>Rating: <span className="font-medium">{minRating.toFixed(1)}+ ⭐</span></div>}
              <div>Years: <span className="font-medium">{yearRange.min} - {yearRange.max}</span></div>
            </div>
          </div>
        </div>

        {/* Custom Slider Styles */}
        <style jsx>{`
          .slider::-webkit-slider-thumb {
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #3B82F6;
            cursor: pointer;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          .slider::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #3B82F6;
            cursor: pointer;
            border: none;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
        `}</style>
      </div>
    </>
  );
};

export default FilterSidebar;